import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

export default function StreamingMessage({ text, isStreaming, onComplete }) {
  const [displayedText, setDisplayedText] = useState('');
  const onCompleteRef = useRef(onComplete);
  const displayedRef = useRef('');
  const completedForRef = useRef(null);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    const full = String(text || '');
    const finish = () => {
      if (completedForRef.current === full) return;
      completedForRef.current = full;
      if (onCompleteRef.current) onCompleteRef.current();
    };

    if (!isStreaming) {
      displayedRef.current = full;
      setDisplayedText(full);
      finish();
      return;
    }

    let currentIndex = 0;
    if (full.startsWith(displayedRef.current)) {
      currentIndex = displayedRef.current.length;
    } else {
      displayedRef.current = '';
      setDisplayedText('');
    }

    if (currentIndex >= full.length) {
      finish();
      return;
    }

    const interval = setInterval(() => {
      if (currentIndex < full.length) {
        currentIndex += 1;
        const next = full.slice(0, currentIndex);
        displayedRef.current = next;
        setDisplayedText(next);
      }
      if (currentIndex >= full.length) {
        clearInterval(interval);
        finish();
      }
    }, 20);

    return () => clearInterval(interval);
  }, [text, isStreaming]);

  const stillTyping = isStreaming && displayedText.length < String(text || '').length;

  return (
    <div className="streaming-message">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ node, ...props }) => <p className="markdown-p" {...props} />,
          ul: ({ node, ...props }) => <ul className="markdown-ul" {...props} />,
          ol: ({ node, ...props }) => <ol className="markdown-ol" {...props} />,
          li: ({ node, ...props }) => <li className="markdown-li" {...props} />,
          strong: ({ node, ...props }) => <strong className="markdown-strong" {...props} />,
          em: ({ node, ...props }) => <em className="markdown-em" {...props} />,
          code: ({ node, inline, className, children, ...props }) => {
            return !inline ? (
              <pre className="markdown-pre">
                <code className={className} {...props}>
                  {children}
                </code>
              </pre>
            ) : (
              <code className="markdown-code-inline" {...props}>
                {children}
              </code>
            );
          },
          pre: ({ node, ...props }) => <pre className="markdown-pre" {...props} />,
          h1: ({ node, ...props }) => <h1 className="markdown-h1" {...props} />,
          h2: ({ node, ...props }) => <h2 className="markdown-h2" {...props} />,
          h3: ({ node, ...props }) => <h3 className="markdown-h3" {...props} />,
          h4: ({ node, ...props }) => <h4 className="markdown-h4" {...props} />,
          h5: ({ node, ...props }) => <h5 className="markdown-h5" {...props} />,
          h6: ({ node, ...props }) => <h6 className="markdown-h6" {...props} />,
          hr: ({ node, ...props }) => <hr className="markdown-hr" {...props} />,
          blockquote: ({ node, ...props }) => <blockquote className="markdown-blockquote" {...props} />,
          a: ({ node, ...props }) => <a className="markdown-a" {...props} />,
        }}
      >
        {displayedText}
      </ReactMarkdown>
      {stillTyping && <span className="streaming-cursor">▊</span>}
    </div>
  );
}
